import React from 'react';
import { createRoot } from 'react-dom/client';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import './assests/fonts/font.css';

/**
 * Webflow Entry Point
 * 
 * This file serves as the UMD bundle entry point for Webflow integration.
 * It exposes a global API under window.MyWebflowWidgets with mount functions
 * for each React component.
 * 
 * Each mount function:
 * 1. Finds the target DOM element using the provided selector
 * 2. Creates a React root for that element
 * 3. Renders the component with provided props
 * 4. Returns an unmount function for cleanup
 * 
 * Usage in Webflow:
 * <script src="https://your-cdn.com/my-widgets.umd.js"></script>
 * <script>
 *   MyWebflowWidgets.mountHeroSection("#hero-root", { title: "Custom Title" });
 *   MyWebflowWidgets.mountAboutSection("#about-root");
 * </script>
 */

// Register GSAP plugins globally for all components
gsap.registerPlugin(ScrollTrigger);

/**
 * Mounts the HeroSection component into a DOM element
 * 
 * @param {string} selector - CSS selector for the target DOM element (e.g., "#hero-root")
 * @param {Object} [props={}] - Props to pass to the HeroSection component
 * @param {string} [props.title] - Hero title text
 * @param {string} [props.subtitle] - Hero subtitle text
 * @param {string} [props.ctaText] - Call-to-action button text
 * @param {Function} [props.onCtaClick] - Callback for CTA button click
 * @returns {Function} Unmount function to cleanup the component
 * 
 * @example
 * const unmount = MyWebflowWidgets.mountHeroSection("#hero-root", {
 *   title: "Welcome to Our Site",
 *   subtitle: "We build amazing things",
 *   ctaText: "Learn More",
 *   onCtaClick: () => console.log("CTA clicked")
 * });
 * 
 * // Later, to unmount:
 * unmount();
 */
function mountHeroSection(selector, props = {}) {
  // Find the target DOM element
  const container = document.querySelector(selector);
  
  if (!container) {
    console.error(`[MyWebflowWidgets] Could not find element with selector: ${selector}`);
    return () => {};
  }

  // Create a React root and render the component
  const root = createRoot(container);
  root.render(<HeroSection {...props} />);

  // Return an unmount function for cleanup
  return () => {
    root.unmount();
  };
}

/**
 * Mounts the AboutSection component into a DOM element
 * 
 * @param {string} selector - CSS selector for the target DOM element (e.g., "#about-root")
 * @param {Object} [props={}] - Props to pass to the AboutSection component
 * @param {string} [props.heading] - Section heading
 * @param {string} [props.description] - Main description text
 * @param {string} [props.mission] - Mission statement text
 * @returns {Function} Unmount function to cleanup the component
 * 
 * @example
 * const unmount = MyWebflowWidgets.mountAboutSection("#about-root", {
 *   heading: "Our Story",
 *   description: "Founded in 2024...",
 *   mission: "We aim to..."
 * });
 * 
 * // Later, to unmount:
 * unmount();
 */
function mountAboutSection(selector, props = {}) {
  // Find the target DOM element
  const container = document.querySelector(selector);
  
  if (!container) {
    console.error(`[MyWebflowWidgets] Could not find element with selector: ${selector}`);
    return () => {};
  }

  // Create a React root and render the component
  const root = createRoot(container);
  root.render(<AboutSection {...props} />);

  // Return an unmount function for cleanup
  return () => {
    root.unmount();
  };
}

/**
 * Global API exposed to Webflow
 * 
 * This object is attached to the window and provides access to all
 * mountable React components in the bundle.
 */
const MyWebflowWidgets = {
  mountHeroSection,
  mountAboutSection,
  
  // Version info for debugging
  version: '1.0.0',
};

// Expose the API globally
window.MyWebflowWidgets = MyWebflowWidgets;

// Log initialization for debugging
console.log('[MyWebflowWidgets] Initialized successfully', {
  version: MyWebflowWidgets.version,
  availableWidgets: Object.keys(MyWebflowWidgets).filter(key => key.startsWith('mount')),
});

export default MyWebflowWidgets;

